import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { NumericFormat } from 'react-number-format'

import Checkbox from '../../../../../../components/form/Checkbox'

import MoneyInput from '../../../../../../components/form/MoneyInput'
import LoadingIcon from '../../../../../../components/LoadingIcon'
import {
    CurrencyList,
    CountryList,
    CountryOfOrigin,
    CountryManufacturer,
    Contract,
    ContractPosition,
} from '../../../types'

import HoverTooltip from '../../../../../../components/HoverTooltip'

type Props = {
    theme: string
    contractPositionGuid: string
    contractPosition: ContractPosition
    handleContractPositionCountryManufacturerPopup: () => void
    onSave: (value: CountryManufacturer | null) => void
}

const ContractPositionCountryManufacturerPopup: React.FC<Props> = (props) => {
    const [selectedCountry, setSelectedCountry] =
        useState<CountryManufacturer | null>(null)
    const [countryList, setCountryList] = useState<CountryList[]>([])
    const [search, setSearch] = useState('')

    const [isLoadingPerformanceDocsCountry, setLoadingPerformanceDocsCountry] =
        useState(false)

    const getCountryList = async (): Promise<CountryList[]> => {
        setLoadingPerformanceDocsCountry(true)
        return fetch(process.env.REACT_APP_API_URL + '/api/backend/v1/oksm', {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на курсы валют')
                }
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => {
                setLoadingPerformanceDocsCountry(false)
            })
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const countries = await getCountryList()
                if (Array.isArray(countries)) {
                    setCountryList(countries)
                } else {
                    console.error('Нет данных:', countries)
                }

                if (props.contractPosition) {
                    setSelectedCountry(
                        props.contractPosition.country_manufacturer
                    )
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    // Фильтрация по поиску
    const filteredList = useMemo(() => {
        const q = (search ?? '').trim().toLowerCase()
        return countryList.filter((c) => {
            const name = String(
                c?.name ?? c?.short_name ?? c?.alpha2 ?? c?.digital_code ?? ''
            )
            return q ? name.toLowerCase().includes(q) : true
        })
    }, [countryList, search])

    // Переключение чекбокса
    const chooseCountry = (code: string) => {
        const src = countryList.find((c) => c.digital_code === code)
        if (!src) return
        const safeName = String(
            src.name ?? src.short_name ?? src.alpha2 ?? src.digital_code ?? ''
        )
        setSelectedCountry({ name: safeName, digital_code: src.digital_code })
    }

    const clearSelection = () => setSelectedCountry(null)

    return (
        <div
            className="fixed z-20 top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
            onClick={props.handleContractPositionCountryManufacturerPopup}
        >
            <div
                className="flex flex-col p-5 rounded-lg w-[800px] h-fit gap-y-5 bg-[#e5e5e5] dark:bg-[#262427]"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[16px]">
                    Страна регистрации производителя товара
                </h2>
                <div className="flex flex-row w-full min-h-10 p-1 gap-1 rounded-md items-center justify-start flex-wrap bg-[#FFFFFF] dark:bg-[#181818]">
                    {selectedCountry ? (
                        <div className="flex flex-row w-fit h-8 p-1 pl-2 gap-2 rounded-md items-center justify-start text-[12.8px] text-[#181818] dark:text-[#FFFFFF] bg-[#ebebeb] dark:bg-[#282629]">
                            {selectedCountry.name}
                            <HoverTooltip tooltipText="Удалить страну">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        clearSelection()
                                    }}
                                    className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                                >
                                    <svg
                                        className="w-3 h-3"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="trash-alt"
                                    >
                                        <path
                                            fill={
                                                props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                        ></path>
                                    </svg>
                                </button>
                            </HoverTooltip>
                        </div>
                    ) : (
                        ''
                    )}
                    <input
                        className="flex p-1 min-w-40 h-8 items-center justify-start focus:outline-none text-[12.8px] text-[#181818] dark:text-[#FFFFFF] bg-[#FFFFFF] dark:bg-[#181818]"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    ></input>
                </div>
                <div className="flex flex-col w-full h-fit items-center justify-center">
                    <div className="flex flex-row w-full gap-1 p-1 py-3 items-center justify-center rounded-t-md text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                        <div className="flex w-5 items-center justify-center "></div>
                        <div className="flex w-10 items-center justify-center ">
                            №
                        </div>
                        <div className="flex flex-1 items-center justify-center ">
                            Краткое наименовние
                        </div>
                        <div className="flex flex-1 items-center justify-center ">
                            Полное наименовние
                        </div>
                        <div className="flex w-32 items-center justify-center ">
                            Код страны
                        </div>
                    </div>
                    <div className="flex flex-col w-full h-[400px] gap-1 p-1 items-start justify-start rounded-b-md overflow-y-auto custom-scrollbar bg-[#e4e6e8] dark:bg-[#1D1C1E]">
                        {isLoadingPerformanceDocsCountry ? (
                            <div className="flex w-full h-full items-start justify-center">
                                <LoadingIcon />
                            </div>
                        ) : (
                            filteredList.map((item, index) => (
                                <div
                                    key={item.guid}
                                    className="flex flex-col w-full h-fit gap-1 p-1 items-center justify-center rounded-md cursor-pointer text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                    onClick={() => {
                                        setSearch('')
                                        chooseCountry(item.digital_code)
                                    }}
                                >
                                    <div className="flex flex-row w-full gap-1 items-center justify-center text-[14px] ">
                                        <div className="flex w-5 items-center justify-center ">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    selectedCountry?.digital_code ===
                                                    item.digital_code
                                                }
                                                onChange={(e) => {
                                                    e.stopPropagation()
                                                    chooseCountry(
                                                        item.digital_code
                                                    )
                                                }}
                                            />
                                        </div>
                                        <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            {index + 1}
                                        </div>
                                        <div className="flex flex-1 items-center justify-start text-[#181818] dark:text-[#FFFFFF]">
                                            {item.short_name}
                                        </div>
                                        <div className="flex flex-1 items-center justify-start text-[#181818] dark:text-[#FFFFFF]">
                                            {item.name}
                                        </div>
                                        <div className="flex w-32 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                            {item.digital_code}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
                <div className="flex flex-row text-right justify-end items-center gap-2">
                    <button
                        className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                        onClick={
                            props.handleContractPositionCountryManufacturerPopup
                        }
                    >
                        Отмена
                    </button>
                    <button
                        className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                        onClick={() => {
                            const country_manufacturer = selectedCountry ?? null
                            props.onSave(country_manufacturer)
                        }}
                    >
                        Сохранить
                    </button>
                </div>
            </div>
        </div>
    )
}
export default ContractPositionCountryManufacturerPopup
